import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Controller, OnSubmit, useForm } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Typography from '@material-ui/core/Typography';
import * as yup from 'yup';
import { Requester } from 'api/utils/Requester';
import Button from 'common/components/Button';
import Checkbox from 'common/components/Checkbox';
import ErrorPanel from 'common/components/ErrorPanel';
import FormLabel from 'common/components/FormLabel';
import TextField from 'common/components/TextField';
import {
    makeAvailabilityStyles,
    makeExpansionPanelClasses,
    makeExpansionPanelDetailsClasses,
    makeExpansionPanelSummaryClasses,
} from 'styles/components/availability';
import { NotificationStateContext } from '../NotificationProvider';
import texts from './texts';

type Props = {
    code: string;
    handleClose: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const AvailabilitySchema = yup.object().shape({
    email: yup.string().email('Adres email musi być poprawny.').required('Pole jest wymagane.'),
    marketing_agreement: yup
        .boolean()
        .oneOf([true], 'Wymagane jest wyrażenie zgody.')
        .required('Pole jest wymagane.'),
    personal_agreement: yup
        .boolean()
        .oneOf([true], 'Wymagane jest wyrażenie zgody.')
        .required('Pole jest wymagane.'),
});

const AvailabilityForm: React.FC<Props> = ({ code, handleClose }) => {
    const [expanded, setExpanded] = useState(false);

    const { readMore } = makeAvailabilityStyles();
    const expansionPanelClasses = makeExpansionPanelClasses();
    const expansionPanelDetailsClasses = makeExpansionPanelDetailsClasses();
    const expansionPanelSummaryClasses = makeExpansionPanelSummaryClasses();

    const { control, errors, handleSubmit, reset, register, setError } = useForm({
        defaultValues: {
            email: '',
            marketing_agreement: false,
            personal_agreement: false,
        },
        validationSchema: AvailabilitySchema,
    });

    const notification = useContext(NotificationStateContext);

    const handleChange = useCallback(() => setExpanded((prevState) => !prevState), [setExpanded]);

    const handleData: OnSubmit<any> = (values) => {
        return new Requester()
            .post('product/notify', { productCode: code, email: values.email })
            .then(() => {
                reset();
                handleClose();
                notification.setConfig({
                    open: true,
                    text: 'Formularz wysłany poprawnie.',
                });
            })
            .catch(({ response }) => {
                const {
                    data: { errors },
                } = response;

                Object.keys(errors).forEach((key: string) =>
                    setError(key, 'invalid', ...errors[key]),
                );
            });
    };

    const disableButton = useMemo(() => Object.keys(errors).length > 0, [
        Object.keys(errors).length,
    ]);

    return (
        <form onSubmit={handleSubmit(handleData)}>
            <Box maxWidth="16rem" width="100%">
                <TextField
                    fullWidth
                    inputRef={register}
                    label="* E-mail"
                    margin="dense"
                    name="email"
                    variant="outlined"
                />
            </Box>
            <ErrorPanel errors={errors} name="email" />
            <Box alignItems="flex-start" display="flex" marginTop={2} maxWidth="25rem">
                <Controller
                    as={<Checkbox />}
                    control={control}
                    id="personal_agreement"
                    name="personal_agreement"
                />
                <FormLabel htmlFor="personal_agreement" required>
                    Wyrażam zgodę na przetwarzanie moich danych osobowych w&nbsp;celu przesłania mi
                    powiadomienia o&nbsp;dostępności wybranych produktów.
                </FormLabel>
            </Box>
            <ErrorPanel errors={errors} name="personal_agreement" />
            <Box alignItems="flex-start" display="flex" marginTop={2} maxWidth="25rem">
                <Controller
                    as={<Checkbox />}
                    control={control}
                    id="marketing_agreement"
                    name="marketing_agreement"
                />
                <FormLabel htmlFor="marketing_agreement" required>
                    Wyrażam zgodę na przesyłanie mi informacji handlowych (o&nbsp;dostępności
                    wybranych produktów) drogą elektroniczną i&nbsp;przy użyciu telekomunikacyjnych
                    urządzeń końcowych (to znaczy e-mailem lub telefonicznie do smartfona, komputera, tabletu, itp.).
                </FormLabel>
            </Box>
            <ExpansionPanel classes={expansionPanelClasses} expanded={expanded} elevation={0}>
                <ExpansionPanelSummary classes={expansionPanelSummaryClasses}>
                    <span className={readMore} onClick={handleChange}>
                        {!expanded && <p>czytaj więcej...</p>}
                        {expanded && <p>czytaj mniej</p>}
                    </span>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails classes={expansionPanelDetailsClasses}>
                    <p dangerouslySetInnerHTML={{ __html: texts.RODO_section_first }} />
                    <p dangerouslySetInnerHTML={{ __html: texts.RODO_section_second }} />
                </ExpansionPanelDetails>
            </ExpansionPanel>
            <ErrorPanel errors={errors} name="marketing_agreement" />
            <Box
                display="flex"
                justifyContent="space-between"
                margin="2.5rem auto 1rem"
                maxWidth="18rem"
                width="100%"
            >
                <Button
                    colorTheme="outlinedPrimary"
                    onClick={handleClose}
                    type="button"
                    variant="outlined"
                >
                    Zamknij
                </Button>
                <Button disabled={disableButton} type="submit">
                    Zapisz
                </Button>
            </Box>
        </form>
    );
};

export default AvailabilityForm;
